import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Modal, Input, Badge } from '../components/Common';
import {
  usePhysios,
  useCreatePhysio,
  useUpdatePhysio,
  useDeletePhysio,
} from '../hooks/usePhysios';
import { physiosApi } from '../services/api';
import type { Physio, CreatePhysioInput, PhysioStatus } from '../types';

function StatusBadge({ status }: { status: PhysioStatus }) {
  const variants: Record<PhysioStatus, { variant: 'success' | 'danger' | 'info'; label: string }> = {
    active: { variant: 'success', label: 'Active' },
    sick: { variant: 'danger', label: 'Sick' },
    unavailable: { variant: 'info', label: 'Unavailable' },
  };
  const { variant, label } = variants[status] || variants.active;
  return <Badge variant={variant}>{label}</Badge>;
}

const SPECIALISMS = [
  'Neurological',
  'Stroke Recovery',
  'Elderly Care',
  "Parkinson's",
  'Mobility Assessment',
];

// Helper to render experience level stars
function ExperienceStars({ level }: { level: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-4 h-4 ${star <= level ? 'text-amber-400' : 'text-gray-200'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function PhysioRow({
  physio,
  onEdit,
  onDelete,
  onCopyStatusLink,
  onGenerateToken,
  isGenerating,
}: {
  physio: Physio;
  onEdit: (physio: Physio) => void;
  onDelete: (physio: Physio) => void;
  onCopyStatusLink: (physio: Physio) => void;
  onGenerateToken: (physioId: number) => void;
  isGenerating: boolean;
}) {
  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => onEdit(physio)}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="font-medium text-gray-900">{physio.name}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <StatusBadge status={physio.status || 'active'} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{physio.postcode}</td>
      <td className="px-6 py-4">
        <div className="flex flex-wrap gap-1">
          {physio.specialisms.map((spec) => (
            <Badge key={spec} variant="info">
              {spec}
            </Badge>
          ))}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <ExperienceStars level={physio.experience_level} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {physio.phone || '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
        {physio.status_token ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onCopyStatusLink(physio);
            }}
          >
            Copy Link
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onGenerateToken(physio.id);
            }}
            isLoading={isGenerating}
          >
            Generate Link
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(physio);
          }}
        >
          Delete
        </Button>
      </td>
    </tr>
  );
}

export function PhysioAdmin() {
  const queryClient = useQueryClient();
  const { data: physios, isLoading } = usePhysios();
  const createPhysio = useCreatePhysio();
  const updatePhysio = useUpdatePhysio();
  const deletePhysio = useDeletePhysio();

  const [showModal, setShowModal] = useState(false);
  const [editingPhysio, setEditingPhysio] = useState<Physio | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Physio | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreatePhysioInput>({
    name: '',
    postcode: '',
    specialisms: [],
    phone: '',
    email: '',
    max_travel_mins: 45,
    experience_level: 3,
  });

  const generateTokenMutation = useMutation({
    mutationFn: (physioId: number) => physiosApi.generateStatusToken(physioId),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['physios'] });
      handleCopy(response.data.status_link);
    },
  });

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess('Link copied!');
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleCopyStatusLink = (physio: Physio) => {
    if (physio.status_token) {
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/status/${physio.status_token}`;
      handleCopy(link);
    }
  };

  const handleCreate = () => {
    setEditingPhysio(null);
    setFormData({
      name: '',
      postcode: '',
      specialisms: [],
      phone: '',
      email: '',
      max_travel_mins: 45,
      experience_level: 3,
    });
    setShowModal(true);
  };

  const handleEdit = (physio: Physio) => {
    setEditingPhysio(physio);
    setFormData({
      name: physio.name,
      postcode: physio.postcode,
      specialisms: physio.specialisms,
      phone: physio.phone || '',
      email: physio.email || '',
      max_travel_mins: physio.max_travel_mins,
      experience_level: physio.experience_level,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingPhysio) {
        await updatePhysio.mutateAsync({ id: editingPhysio.id, data: formData });
      } else {
        await createPhysio.mutateAsync(formData);
      }
      setShowModal(false);
    } catch (error) {
      console.error('Failed to save physio:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deletePhysio.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete physio:', error);
    }
  };

  const toggleSpecialism = (spec: string) => {
    if (formData.specialisms.includes(spec)) {
      setFormData({
        ...formData,
        specialisms: formData.specialisms.filter((s) => s !== spec),
      });
    } else {
      setFormData({
        ...formData,
        specialisms: [...formData.specialisms, spec],
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Physio Administration</h1>
        <Button onClick={handleCreate}>Add Physio</Button>
      </div>

      <Card>
        {isLoading ? (
          <p className="text-gray-500">Loading physios...</p>
        ) : !physios || physios.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No physios registered yet</p>
            <Button onClick={handleCreate}>Add First Physio</Button>
          </div>
        ) : (
          <>
            {copySuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800">
                {copySuccess}
              </div>
            )}
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Postcode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Specialisms
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Experience
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {physios.map((physio) => (
                  <PhysioRow
                    key={physio.id}
                    physio={physio}
                    onEdit={handleEdit}
                    onDelete={setDeleteConfirm}
                    onCopyStatusLink={handleCopyStatusLink}
                    onGenerateToken={(id) => generateTokenMutation.mutate(id)}
                    isGenerating={generateTokenMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingPhysio ? 'Edit Physio' : 'Add New Physio'}
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Dr. John Smith"
          />
          <Input
            label="Postcode"
            value={formData.postcode}
            onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
            placeholder="GU1 1AA"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Specialisms</label>
            <div className="flex flex-wrap gap-2">
              {SPECIALISMS.map((spec) => (
                <button
                  key={spec}
                  type="button"
                  onClick={() => toggleSpecialism(spec)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    formData.specialisms.includes(spec)
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {spec}
                </button>
              ))}
            </div>
          </div>

          <Input
            label="Phone"
            value={formData.phone || ''}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="07700 123456"
          />
          <Input
            label="Email"
            type="email"
            value={formData.email || ''}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="physio@example.com"
          />
          <Input
            label="Max Travel Time (minutes)"
            type="number"
            value={formData.max_travel_mins || 45}
            onChange={(e) =>
              setFormData({ ...formData, max_travel_mins: parseInt(e.target.value) || 45 })
            }
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Experience Level
            </label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setFormData({ ...formData, experience_level: level })}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <svg
                    className={`w-8 h-8 ${
                      level <= (formData.experience_level || 3)
                        ? 'text-amber-400'
                        : 'text-gray-200 hover:text-amber-200'
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </button>
              ))}
              <span className="ml-2 text-sm text-gray-500">
                {formData.experience_level || 3} / 5
              </span>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              isLoading={createPhysio.isPending || updatePhysio.isPending}
              disabled={!formData.name || !formData.postcode}
            >
              {editingPhysio ? 'Save Changes' : 'Add Physio'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? This action
            cannot be undone.
          </p>
          <div className="flex justify-end space-x-2">
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deletePhysio.isPending}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
